/**
 * Ilovaning qaysi muhitda ishlayotganini aniqlovchi global konstantalar.
 * Barcha domen/muhit tekshiruvlari shu fayldan import qilinadi.
 *
 * NOTE: auth, brand token va lazimal owner-app aniqlash uchun
 * `src/hosts/` registry kanonik manba. Bu fayl leaf module sifatida saqlanadi
 * (host registry shu fayldan import qiladi, teskari emas) — circular import dan
 * saqlanish uchun shunday. Komponentlarda oddiy domen tekshiruvi uchun
 * `IS_CLICK_DOMAIN` ishlatish davom etadi; auth/brand logikasi uchun esa
 * `import { getActiveHost, getActiveBrandId } from "@/hosts"` afzal.
 */

import { getCookie } from "@/lib/cookies";

const { hostname } = window.location;

/** click.insonuchun.uz — Telegram orqali emas, to'g'ridan-to'g'ri kirish */
export const IS_CLICK_DOMAIN = hostname === "click.insonuchun.uz" || hostname === "ihma-bot-test.netlify.app";
export const IS_PAYMEE_DOMAIN = hostname === "payme.insonuchun.uz" ;
export const IS_ZOOMRAD_DOMAIN = hostname === "zoomrad.insonuchun.uz" || hostname === "localhost";
export const IS_PAYNET_DOMAIN = hostname === "paynet.insonuchun.uz";

/** Telegram WebApp ichida ishlamoqda */
export const IS_TELEGRAM = Boolean(window.Telegram?.WebApp?.initData);

/** Mahalliy ishlanma muhiti */
export const IS_DEV = import.meta.env.DEV;

export const COOKIES = {
  CLICK_WEB_SESSION: "click-web-session",
  WEB_SESSION: "web-session",
  CLICK_STYLE: "click-style",
  STYLE: "style",
  CLICK_THEME: "click-theme",
  THEME: "theme",
  CLICK_APP_VERSION: "click-appversion",
  CLICK_LANGUAGE: "click-language",
};

export const LOCAL_LANG_KEY = "Lang";
export const DEFAULT_LANG = "uz_latn";

/** Mini app ichida qo'llaniladigan yagona (canonical) til kalitlari */
export const APP_LANGS = ["uz_latn", "ru", "en"] as const;
export type AppLang = (typeof APP_LANGS)[number];

/**
 * Click super app cookie sidan til qiymatini o'qib, bizning lang key ga aylantiradi.
 * click-language: "uz" → "uz_latn" | "ru" → "ru" | "en" → "en"
 * Cookie yo'q yoki noma'lum qiymat bo'lsa null qaytaradi.
 */
/**
 * Platforma/bosh app kalitlari -> mini app canonical kalitiga map.
 * Keyinchalik Paymee/Zomrad kabi app lar aliaslari shu yerga qo'shiladi.
 */
export const allAppLangKeys: Record<AppLang, string[]> = {
  uz_latn: ["uz_latn", "uz", "uzb", "uzbek", "uz-latn"],
  ru: ["ru", "rus", "ru-ru"],
  en: ["en", "eng", "en-us", "en-gb"],
};

const ALIAS_TO_CANONICAL = Object.entries(allAppLangKeys).reduce(
  (acc, [canonical, aliases]) => {
    for (const alias of aliases) acc[alias.toLowerCase()] = canonical as AppLang;
    return acc;
  },
  {} as Record<string, AppLang>
);

/** Tashqi app dan kelgan ixtiyoriy key ni canonical key ga o'tkazadi */
export function normalizeLangKey(raw: string | null | undefined): AppLang | null {
  if (!raw) return null;
  return ALIAS_TO_CANONICAL[raw.trim().toLowerCase()] ?? null;
}

/** Mini app ichida qo'llaniladigan yagona (canonical) theme kalitlari */
export const APP_THEMES = ["dark", "light", "auto"] as const;
export type AppTheme = (typeof APP_THEMES)[number];

/**
 * Platforma/bosh app theme kalitlari -> mini app canonical kalitiga map
 * (til uchun `allAppLangKeys` bilan bir xil g'oya). Turli super app'lar bir xil
 * mavzuni har xil nomlashi mumkin — mas. "white" == "light", "black" == "dark",
 * "system" == "auto" — shu yerda barchasi bitta qiymatga keltiriladi.
 */
export const allAppThemeKeys: Record<AppTheme, string[]> = {
  dark: ["dark", "black", "night", "dim", "darkmode", "dark-mode"],
  light: ["light", "white", "day", "bright", "lightmode", "light-mode"],
  auto: ["auto", "system", "default", "os", "device"],
};

const THEME_ALIAS_TO_CANONICAL = Object.entries(allAppThemeKeys).reduce(
  (acc, [canonical, aliases]) => {
    for (const alias of aliases) acc[alias.toLowerCase()] = canonical as AppTheme;
    return acc;
  },
  {} as Record<string, AppTheme>,
);

/** Tashqi app dan kelgan ixtiyoriy theme key ni canonical key ga o'tkazadi */
export function normalizeThemeKey(raw: string | null | undefined): AppTheme | null {
  if (!raw) return null;
  return THEME_ALIAS_TO_CANONICAL[raw.trim().toLowerCase()] ?? null;
}

/**
 * Mavzu tanlovi saqlanadigan localStorage kaliti — `useTheme` (`getInitialTheme`)
 * va super app integratsiyalari (Paynet login javobi) shu yagona manbadan
 * foydalanadi.
 */
export const THEME_STORAGE_KEY = "theme";

/**
 * Super app login javobidan kelgan mavzuni (mas. Paynet `theme: "dark"`) canonical
 * qiymatga keltirib localStorage'ga yozadi. `getInitialTheme()` shu kalitni
 * o'qigani uchun mavzu keyingi boot'da avtomatik qo'llanadi — theme boot logikasiga
 * tegilmaydi. Noma'lum/bo'sh qiymat jimgina e'tiborsiz qoldiriladi.
 */
export function persistPreferredTheme(raw: string | null | undefined): void {
  const theme = normalizeThemeKey(raw);
  if (!theme) return;
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    /* localStorage yo'q (private mode / disabled) — jimgina o'tkazamiz */
  }
}

/* ──────────────────────────────────────────────────────────────────────────
 * Super app "launch" paramlari (URL query orqali).
 *
 * Super app mini app'ni allaqachon tanlangan til/mavzu bilan ochadi, mas:
 *   {brand}.insonuchun.uz?lang=uz&theme=dark
 * Vazifa: shu paramlar bo'lsa, clientda aynan shu til va mavzuni qo'yish.
 * ────────────────────────────────────────────────────────────────────────── */

const LAUNCH_PARAMS = ["lang", "theme"] as const;

/** URL query'dan bitta parametr (window yo'q/parse xatosida null). */
function getUrlParam(name: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    return new URLSearchParams(window.location.search).get(name);
  } catch {
    return null;
  }
}

/** Super app `?lang=` paramidan canonical til (yaroqli bo'lsa; `uz`→`uz_latn`). */
export function getUrlLang(): AppLang | null {
  return normalizeLangKey(getUrlParam("lang"));
}

/** Super app `?theme=` paramidan canonical mavzu (aliaslar bilan; `white`→`light`). */
export function getUrlTheme(): AppTheme | null {
  return normalizeThemeKey(getUrlParam("theme"));
}

/**
 * Launch paramlarni URL'dan olib tashlaydi (bir marta, qo'llangandan keyin).
 * Aks holda reload/ulashishda ular foydalanuvchining app ichida o'zgartirgan
 * til/mavzu tanlovi ustidan qayta yozilib qolar edi.
 */
export function stripLaunchParamsFromUrl(): void {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  let changed = false;
  for (const key of LAUNCH_PARAMS) {
    if (url.searchParams.has(key)) {
      url.searchParams.delete(key);
      changed = true;
    }
  }
  if (!changed) return;
  const qs = url.searchParams.toString();
  window.history.replaceState({}, "", `${url.pathname}${qs ? `?${qs}` : ""}${url.hash}`);
}

/** Click cookie -> canonical key */
export function getClickLang(): AppLang | null {
  return normalizeLangKey(getCookie(COOKIES.CLICK_LANGUAGE));
}

/**
 * Click super app cookie sidan theme qiymatini o'qiydi.
 * click-theme: "dark" | "light" — boshqa qiymat bo'lsa null.
 */
export function getClickTheme(): "dark" | "light" | null {
  const raw = getCookie(COOKIES.CLICK_THEME);
  if (raw === "dark" || raw === "light") return raw;
  return null;
}

export type OwnerApp = "click" | "bot" | "web";

/**
 * Hozirgi runtime qaysi owner app ekanini aniqlaydi.
 * Priority: click domain > telegram bot > oddiy web.
 */
export function detectOwnerApp(): OwnerApp {
  if (IS_CLICK_DOMAIN) return "click";
  if (IS_TELEGRAM) return "bot";
  return "web";
}

/** Bot rejimida localStorage dagi tanlangan til olinadi */
function getBotLang(): AppLang | null {
  return normalizeLangKey(localStorage.getItem(LOCAL_LANG_KEY));
}

/** Bosh app/platform bo'yicha startup tilni topadi */
export function resolveStartupLang(owner: OwnerApp = detectOwnerApp()): AppLang {
  // Super app URL parami eng yuqori ustuvorlikka ega — tashqi ilova ochilish
  // paytida aynan shu tilni ko'rsatgan (owner cookie/localStorage'idan ustun).
  const fromUrl = getUrlLang();
  if (fromUrl) return fromUrl;

  const byOwner =
    owner === "click"
      ? getClickLang()
      : owner === "bot"
        ? getBotLang()
        : normalizeLangKey(localStorage.getItem(LOCAL_LANG_KEY));
  return byOwner ?? DEFAULT_LANG;
}

/** Startup tilni topadi va localStorage ni canonical key bilan sinxron qiladi */
export function resolveAndPersistStartupLang(owner: OwnerApp = detectOwnerApp()): AppLang {
  const lang = resolveStartupLang(owner);
  localStorage.setItem(LOCAL_LANG_KEY, lang);
  return lang;
}

export const numberCurrencyFormat = (value: number) => {
  // if (value === undefined || value === null) return "0.00";
  // return (
  //   value?.toLocaleString("uz-UZ", {
  //     minimumFractionDigits: 0,
  //     maximumFractionDigits: 0,
  //   }) || "0"
  // );
  if (value === undefined || value === null) return "0.00";

  return new Intl.NumberFormat("fr-FR", {
    // fr-FR standarti bo'shliq ishlatadi
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
    .format(value)
    .replace(/\s/g, " "); // Maxsus bo'shliqlarni oddiy bo'shliqqa aylantirish
};